import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MathBlock } from "../components/MathRender";
import { Microscope, Plus, ArrowLeft, UserPlus, CheckCircle2, Lock } from "lucide-react";

function RoomDetail({ roomId, onBack }) {
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState("");
  const [ms, setMs] = useState({ title: "", description: "", amount: 0 });

  const load = useCallback(() => {
    api.get(`/research/rooms/${roomId}`).then((r) => setRoom(r.data)).catch(() => toast.error("Private room"));
    api.get(`/research/rooms/${roomId}/pending`).then((r) => setPending(r.data)).catch(() => setPending([]));
  }, [roomId]);
  useEffect(() => { load(); }, [load]);

  if (!room) return <div className="py-16 text-center text-slate-500">Loading room…</div>;
  const isSponsor = user?.id === room.sponsor_id || user?.role === "admin";

  const invite = async () => {
    try {
      const { data } = await api.post(`/research/rooms/${roomId}/invite`, { email });
      toast.success(data.status === "pending" ? "Invitation saved — they'll join automatically when they register" : "Collaborator invited");
      setEmail(""); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };
  const addMilestone = async () => {
    if (!ms.title) { toast.error("Milestone title required"); return; }
    try { await api.post(`/research/rooms/${roomId}/milestones`, { ...ms, amount: Number(ms.amount) }); toast.success("Milestone added"); setMs({ title: "", description: "", amount: 0 }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const approve = async (mid) => {
    try { await api.post(`/research/rooms/${roomId}/milestones/${mid}/approve`); toast.success("Milestone approved & paid out (simulated)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };

  return (
    <div data-testid="research-room-detail">
      <button onClick={onBack} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All rooms</button>
      <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#D4AF37]" /><span className="eyebrow">Private research room</span></div>
      <h2 className="mt-2 font-display text-3xl text-slate-900">{room.title}</h2>
      <p className="mt-2 text-slate-600">{room.abstract}</p>
      <div className="axiom-card mt-5 p-5"><p className="eyebrow mb-1">IP &amp; rights</p><p className="text-sm text-slate-600">{room.ip_terms}</p></div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <p className="eyebrow mb-3">Collaborators</p>
          <div className="space-y-2">
            {room.collaborators?.map((c) => (
              <div key={c.user_id} className="axiom-card flex items-center justify-between p-3" data-testid={`collab-${c.user_id}`}>
                <span className="text-sm text-slate-700">{c.name}</span><span className="text-xs text-slate-400">{c.email}</span>
              </div>
            ))}
            {(!room.collaborators || room.collaborators.length === 0) && <p className="text-sm text-slate-500">No collaborators yet.</p>}
          </div>
          {isSponsor && pending.length > 0 && (
            <div className="mt-4" data-testid="pending-invites">
              <p className="eyebrow mb-2">Pending invitations</p>
              <div className="space-y-2">
                {pending.map((pi) => (
                  <div key={pi.id} className="flex items-center justify-between rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-3 py-2" data-testid={`pending-${pi.id}`}>
                    <span className="text-sm text-slate-700">{pi.email}</span>
                    <span className="text-xs font-semibold text-amber-700">Awaiting sign-up</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isSponsor && (
            <div className="mt-3 flex gap-2">
              <input data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="collaborator email"
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#E61A27]" />
              <button data-testid="invite-btn" onClick={invite} className="btn-ghost !px-4 !py-2 text-sm"><UserPlus className="h-4 w-4" /> Invite</button>
            </div>
          )}
        </div>

        <div>
          <p className="eyebrow mb-3">Milestones</p>
          <div className="space-y-2">
            {room.milestones?.map((m) => (
              <div key={m.id} className="axiom-card p-4" data-testid={`milestone-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900">{m.title}</span>
                  <span className="text-sm text-emerald-700">${Number(m.amount).toLocaleString()}</span>
                </div>
                {m.description && <p className="mt-1 text-xs text-slate-500">{m.description}</p>}
                <div className="mt-2 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${m.status === "approved" ? "text-emerald-700" : "text-amber-700"}`}>{m.status === "approved" ? "Approved" : "Pending"}</span>
                  {isSponsor && m.status !== "approved" && (
                    <button data-testid={`approve-${m.id}`} onClick={() => approve(m.id)} className="inline-flex items-center gap-1 text-xs text-[#E61A27] hover:underline"><CheckCircle2 className="h-4 w-4" /> Approve &amp; pay</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isSponsor && (
            <div className="axiom-card mt-3 space-y-2 p-4">
              <input data-testid="ms-title" value={ms.title} onChange={(e) => setMs({ ...ms, title: e.target.value })} placeholder="Milestone title"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#E61A27]" />
              <input data-testid="ms-amount" type="number" value={ms.amount} onChange={(e) => setMs({ ...ms, amount: e.target.value })} placeholder="Amount (USD)"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#E61A27]" />
              <button data-testid="ms-add" onClick={addMilestone} className="btn-ghost !py-2 text-sm"><Plus className="h-4 w-4" /> Add milestone</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Research() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", abstract: "", ip_terms: "Sponsor holds commercial rights; collaborators retain attribution and co-authorship." });

  const load = useCallback(() => { api.get("/research/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try { const { data } = await api.post("/research/rooms", form); toast.success("Research room created"); setCreating(false); setForm({ title: "", abstract: "", ip_terms: form.ip_terms }); load(); setActive(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="research-page">
      {active ? (
        <RoomDetail roomId={active} onBack={() => { setActive(null); load(); }} />
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow mb-2">Research</p>
              <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl flex items-center gap-3"><Microscope className="h-8 w-8 text-[#E61A27]" /> Sponsored Research Studio</h1>
              <p className="mt-3 max-w-2xl text-slate-600">Private rooms for sponsored investigations — invite collaborators, set milestones, and agree explicit IP terms up front.</p>
            </div>
            {user?.role === "customer" && <button data-testid="new-room-btn" onClick={() => setCreating(!creating)} className="btn-crimson"><Plus className="h-4 w-4" /> New room</button>}
          </div>

          {creating && (
            <div className="axiom-card mt-6 space-y-3 p-6" data-testid="new-room-form">
              <input data-testid="room-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Room title"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]" />
              <textarea data-testid="room-abstract" rows={2} value={form.abstract} onChange={(e) => setForm({ ...form, abstract: e.target.value })} placeholder="Abstract / scope"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]" />
              <textarea data-testid="room-ip" rows={2} value={form.ip_terms} onChange={(e) => setForm({ ...form, ip_terms: e.target.value })} placeholder="IP terms"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-[#E61A27]" />
              <button data-testid="room-create" onClick={create} className="btn-crimson !py-2 text-sm">Create room</button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {rooms.map((r) => (
              <button key={r.id} data-testid={`room-${r.id}`} onClick={() => setActive(r.id)} className="axiom-card p-6 text-left">
                <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#D4AF37]" /><span className="text-xs text-slate-400">Private · {r.collaborator_ids?.length || 0} collaborators</span></div>
                <h3 className="mt-2 font-display text-xl text-slate-900">{r.title}</h3>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{r.abstract}</p>
                <p className="mt-3 text-xs text-slate-400">{r.milestones?.length || 0} milestones · sponsored by {r.sponsor_name}</p>
              </button>
            ))}
          </div>
          {rooms.length === 0 && <p className="mt-8 text-slate-500" data-testid="research-empty">No research rooms you can access yet.</p>}
        </>
      )}
    </div>
  );
}
